import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Store, Clock, ChefHat, Heart, ArrowUpDown, ChevronDown, BadgeCheck } from 'lucide-react';
import { getJpegFallbackUrl, getRestaurantBannerUrl, getRestaurantDetails } from '../../../services/api';
import { RatingStarsFa, getDisplayRating } from '../../../utils/ratingStars';
import { useFavoriteAuth } from '../../../hooks/useFavoriteAuth';
import { loadFavorites } from '../../../utils/favoritesManager';
import LoginPopup from '../../LoginPopup';
import namkeFallback from '../../../assets/namke-fallback.png';

const PRIMARY = 'rgb(5, 70, 37)';

const ServiceBadge = ({ children, className = '' }) => (
  <span
    className={`text-[11px] font-semibold px-2 py-0.5 rounded ${className}`}
  >
    {children}
  </span>
);

const RestaurantsSection = ({
  restaurants,
  restaurantsLoading,
  filteredRestaurants,
  onRestaurantCardClick,
  restaurantSortBy,
  showRestaurantSortDropdown,
  onRestaurantSortChange,
  onToggleRestaurantSortDropdown,
  serviceMode = 'all',
  onServiceModeChange,
}) => {
  const { t } = useTranslation();
  const [detailsById, setDetailsById] = useState({});
  const [favoritedIds, setFavoritedIds] = useState(() => new Set());
  const {
    isAuthenticated,
    showLoginPopup,
    handleFavoriteAction,
    handleLoginSuccess,
    closeLoginPopup,
  } = useFavoriteAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      setFavoritedIds(new Set());
      return;
    }
    let cancelled = false;
    loadFavorites()
      .then((favorites) => {
        if (cancelled) return;
        const ids = favorites
          .filter((fav) => fav.favorited_object_type === 'restaurant')
          .map((fav) => fav.favorited_object_id);
        setFavoritedIds(new Set(ids));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const toggleFavorite = async (restaurantId) => {
    const isFav = favoritedIds.has(restaurantId);
    const success = await handleFavoriteAction(isFav ? 'remove' : 'add', restaurantId, 'restaurant');
    if (success) {
      setFavoritedIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.delete(restaurantId);
        else next.add(restaurantId);
        return next;
      });
    }
  };

  const restaurantIds = useMemo(
    () => (Array.isArray(filteredRestaurants) ? filteredRestaurants.map((r) => r?.id).filter(Boolean) : []),
    [filteredRestaurants],
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!restaurantIds.length) return;
      const toFetch = restaurantIds.filter((id) => !detailsById[id]);
      if (!toFetch.length) return;
      const results = await Promise.all(
        toFetch.map(async (id) => {
          try { return [id, await getRestaurantDetails(id)]; }
          catch { return [id, null]; }
        }),
      );
      if (cancelled) return;
      setDetailsById((prev) => {
        const next = { ...prev };
        results.forEach(([id, d]) => { next[id] = d; });
        return next;
      });
    };
    run();
    return () => { cancelled = true; };
  }, [restaurantIds, detailsById]);

  const restaurantSortOptions = [
    { value: 'name', label: t('home.sortOptions.name') },
    { value: 'rating', label: t('home.sortOptions.rating') },
    { value: 'popularity', label: t('home.sortOptions.popularity') },
  ];

  const count = filteredRestaurants?.length ?? 0;

  return (
    <>
      {/* Barre compteur + tri */}
      <div className="flex items-center justify-between mb-5">
        <span className="text-sm text-namke-black/55">
          {t('home.restaurants.count', { count })}
          {serviceMode === 'pickup' ? ` ${t('home.restaurants.withPickup')}` : serviceMode === 'delivery' ? ` ${t('home.restaurants.withDelivery')}` : ''}
        </span>
        <div className="relative">
          <button
            onClick={onToggleRestaurantSortDropdown}
            className="flex items-center gap-2 px-3 py-2 border border-namke-darkGreen/20 rounded-full hover:bg-primary-50 text-sm text-namke-black/75"
          >
            <ArrowUpDown className="w-4 h-4 text-namke-black/40" />
            {restaurantSortOptions.find(o => o.value === restaurantSortBy)?.label || t('home.sort')}
            <ChevronDown className="w-3.5 h-3.5 text-namke-black/40" />
          </button>
          {showRestaurantSortDropdown && (
            <div className="absolute right-0 mt-1 w-44 bg-namke-white border border-namke-darkGreen/15 rounded-2xl shadow-lg z-10">
              {restaurantSortOptions.map((o) => (
                <button
                  key={o.value}
                  onClick={() => onRestaurantSortChange(o.value)}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-primary-50 first:rounded-t-2xl last:rounded-b-2xl"
                >
                    {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {restaurantsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-namke-white rounded-2xl border border-namke-darkGreen/10 overflow-hidden animate-pulse">
              <div className="flex flex-col sm:flex-row">
                <div className="w-full sm:w-1/3 h-48 bg-namke-cream flex-shrink-0" />
                <div className="flex-1 p-6 space-y-3">
                  <div className="h-5 bg-namke-cream rounded w-2/3" />
                  <div className="h-3 bg-namke-cream rounded w-1/3" />
                  <div className="h-3 bg-namke-cream rounded w-1/2" />
                  <div className="h-9 bg-namke-cream rounded-full w-32 mt-4" />
                </div>
              </div>
            </div>
          ))
        ) : !filteredRestaurants || filteredRestaurants.length === 0 ? (
          <div className="text-center py-14">
            <Store className="h-10 w-10 text-namke-darkGreen/25 mx-auto mb-3" />
            <p className="text-namke-black/50 text-sm">
              {serviceMode === 'pickup'
                ? t('home.restaurants.noPickup')
                : t('home.restaurants.noResults')}
            </p>
          </div>
        ) : (
          filteredRestaurants.map((restaurant) => {
            const displayRating = getDisplayRating(restaurant, 0);
            const withDetail = detailsById[restaurant.id]
              ? { ...restaurant, ...(detailsById[restaurant.id] || {}) }
              : restaurant;
            const primaryImage =
              getRestaurantBannerUrl(withDetail, 'original') ||
              getRestaurantBannerUrl(withDetail, 'large');
            const offersPickup = withDetail.accept_pickup === true;
            const isOpen = restaurant.is_active !== false;

            return (
              <div
                key={restaurant.id}
                className="bg-namke-white rounded-2xl overflow-hidden transition-all duration-200 cursor-pointer border border-namke-darkGreen/10"
                onClick={() => onRestaurantCardClick(restaurant)}
              >
                <div className="flex flex-col sm:flex-row">
                  {/* Image */}
                  <div className="w-full sm:w-[38%] lg:w-[30%] relative h-52 sm:h-auto flex-shrink-0 overflow-hidden">
                    <img
                      src={primaryImage || namkeFallback}
                      alt={restaurant.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        const fb = getJpegFallbackUrl(primaryImage);
                        if (fb && e.target.dataset.fb !== '1') {
                          e.target.dataset.fb = '1';
                          e.target.src = fb;
                          return;
                        }
                        e.target.src = namkeFallback;
                      }}
                    />
                    <button
                      className="absolute top-3 right-3 bg-white/90 p-1.5 rounded-full hover:bg-red-50 transition-colors"
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(restaurant.id); }}
                      aria-label={favoritedIds.has(restaurant.id) ? t('home.removeFavorite') : t('home.addFavorite')}
                    >
                      <Heart
                        size={14}
                        className={favoritedIds.has(restaurant.id) ? 'text-red-500' : 'text-gray-400 hover:text-red-400 transition-colors'}
                        fill={favoritedIds.has(restaurant.id) ? 'currentColor' : 'none'}
                      />
                    </button>
                  </div>

                  {/* Contenu */}
                  <div className="flex-1 p-5 flex flex-col justify-between">
                    <div>
                      {/* Nom */}
                      <h3 className="font-display text-xl font-bold mb-2 flex items-center gap-1.5" style={{ color: PRIMARY }}>
                        {restaurant.name}
                        {restaurant.is_certified && (
                          <BadgeCheck
                            size={18}
                            className="text-[rgb(5,70,37)] shrink-0"
                            fill="currentColor"
                            stroke="white"
                            title={t('home.restaurants.certified')}
                          />
                        )}
                      </h3>

                      {/* Badges service — sobres, sans icônes */}
                      <div className="flex items-center flex-wrap gap-1.5 mb-3">
                        <ServiceBadge className="bg-primary-50 text-[rgb(5,70,37)] !rounded-full">
                          {t('home.restaurants.delivery')}
                        </ServiceBadge>
                        {offersPickup && (
                          <ServiceBadge className="bg-primary-50 text-[rgb(5,70,37)] !rounded-full">
                            {t('home.restaurants.pickup')}
                          </ServiceBadge>
                        )}
                        <ServiceBadge className={`!rounded-full ${isOpen ? 'bg-namke-cream text-namke-olive' : 'bg-namke-cream text-namke-black/35'}`}>
                          {isOpen ? t('home.restaurants.open') : t('home.restaurants.closed')}
                        </ServiceBadge>
                      </div>

                      {/* Note */}
                      <div className="flex items-center gap-2 mb-2">
                        <RatingStarsFa rating={displayRating} size="sm" />
                        <span className="text-sm font-semibold tabular-nums" style={{ color: PRIMARY }}>
                          {displayRating > 0 ? displayRating.toFixed(1) : '—'}
                        </span>
                        {restaurant.review_count != null && (
                          <span className="text-xs text-namke-black/35">{t('home.restaurants.reviewCount', { count: restaurant.review_count })}</span>
                        )}
                      </div>

                      {/* Cuisine + gamme prix */}
                      <div className="flex items-center gap-1.5 mb-3 text-sm" style={{ color: PRIMARY }}>
                        <ChefHat size={14} />
                        <span className="font-medium">{restaurant.cuisine_type || '—'}</span>
                        <span className="text-namke-black/25">·</span>
                        <span className="font-medium">
                          {restaurant.minimum_order != null
                            ? restaurant.minimum_order >= 20 ? '€€€' : restaurant.minimum_order >= 10 ? '€€' : '€'
                            : '€€'}
                        </span>
                      </div>

                      <p className="text-sm text-namke-black/55 leading-relaxed line-clamp-2 mb-4">
                        {restaurant.description || t('home.restaurants.defaultDescription')}
                      </p>
                    </div>

                    <Link
                      to={`/restaurant/${restaurant.id}`}
                      className="inline-flex items-center justify-center py-2 px-5 rounded-full text-sm font-semibold text-white self-start transition-opacity hover:opacity-90"
                      style={{ backgroundColor: PRIMARY }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('home.restaurants.viewMenu')}
                    </Link>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <LoginPopup isOpen={showLoginPopup} onClose={closeLoginPopup} onLoginSuccess={handleLoginSuccess} />
    </>
  );
};

export default RestaurantsSection;
